import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  Tabs,
  Tab,
  Divider,
  Chip,
  Stack,
  TextField,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  alpha,
} from '@mui/material';
import {
  Person,
  Business,
  CreditCard,
  Security,
  Notifications,
  Edit,
  Download,
  Visibility,
  CheckCircle,
  Warning,
  TrendingUp,
  People,
  Email,
  Phone,
  Receipt,
  Settings,
  ExitToApp,
  Lock,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { SERVICE_TIERS, formatPrice, getTier } from '../lib/serviceTiers';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div hidden={value !== index} {...other}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

// Mock data - in production, this would come from API
const mockAccount = {
  tier: 'professional',
  company: {
    name: 'Acme Corporation',
    website: 'https://acme.com',
    industry: 'Technology',
  },
  user: {
    firstName: 'John',
    lastName: 'Doe',
    email: 'john@acme.com',
    phone: '+1 (555) 123-4567',
    avatar: null,
  },
  subscription: {
    status: 'active',
    currentPeriodEnd: '2025-01-09',
    cancelAtPeriodEnd: false,
  },
  usage: {
    leadsGenerated: 847,
    leadsLimit: 1000,
    emailsSent: 2340,
    callsMade: 156,
  },
  invoices: [
    { id: 'inv_001', date: '2024-12-01', amount: 39999, status: 'paid' },
    { id: 'inv_002', date: '2024-11-01', amount: 39999, status: 'paid' },
    { id: 'inv_003', date: '2024-10-01', amount: 749900, status: 'paid' },
  ],
};

const AccountPage: React.FC = () => {
  const { user, logout } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [account, setAccount] = useState(mockAccount);
  const [editDialog, setEditDialog] = useState<string | null>(null);
  const [changePasswordDialog, setChangePasswordDialog] = useState(false);
  
  const currentTier = getTier(account.tier);
  
  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
      case 'paid':
        return 'success';
      case 'past_due':
      case 'unpaid':
        return 'warning';
      case 'canceled':
      case 'failed':
        return 'error';
      default:
        return 'default';
    }
  };

  // Overview Tab
  const renderOverview = () => (
    <Grid container spacing={3}>
      {/* Current Plan Card */}
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
              <Typography variant="h6" fontWeight={600}>
                Current Plan
              </Typography>
              <Chip
                label={account.subscription.status.toUpperCase()}
                color={getStatusColor(account.subscription.status) as any}
                size="small"
              />
            </Box>
            
            {currentTier && (
              <>
                <Typography variant="h4" fontWeight={800} gutterBottom>
                  {currentTier.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={2}>
                  {currentTier.tagline}
                </Typography>
                
                <Box display="flex" alignItems="baseline" gap={1} mb={2}>
                  <Typography variant="h5" fontWeight={700}>
                    {formatPrice(currentTier.monthlyFee)}
                  </Typography>
                  <Typography color="text.secondary">/month</Typography>
                </Box>
                
                <Typography variant="body2" color="text.secondary">
                  Renews on {new Date(account.subscription.currentPeriodEnd).toLocaleDateString()}
                </Typography>
              </>
            )}
            
            <Box mt={3} display="flex" gap={2}>
              <Button variant="outlined" size="small">
                Change Plan
              </Button>
              <Button variant="text" size="small" color="error">
                Cancel Subscription
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Grid>
      
      {/* Usage Stats Card */}
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" fontWeight={600} mb={3}>
              Usage This Month
            </Typography>
            
            <Box mb={3}>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2">Leads Generated</Typography>
                <Typography variant="body2" fontWeight={600}>
                  {account.usage.leadsGenerated} / {account.usage.leadsLimit}
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={(account.usage.leadsGenerated / account.usage.leadsLimit) * 100}
                sx={{ height: 8, borderRadius: 4 }}
              />
            </Box>
            
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Box textAlign="center" p={2} bgcolor="action.hover" borderRadius={2}>
                  <Email color="primary" />
                  <Typography variant="h5" fontWeight={700}>
                    {account.usage.emailsSent.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Emails Sent
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box textAlign="center" p={2} bgcolor="action.hover" borderRadius={2}>
                  <Phone color="primary" />
                  <Typography variant="h5" fontWeight={700}>
                    {account.usage.callsMade}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Calls Made
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>
      
      {/* Quick Stats */}
      <Grid item xs={12}>
        <Grid container spacing={2}>
          {[
            { icon: <People />, label: 'Total Leads', value: '3,245' },
            { icon: <TrendingUp />, label: 'Conversion Rate', value: '12.4%' },
            { icon: <Receipt />, label: 'Total Spent', value: '$8,699' },
          ].map((stat, idx) => (
            <Grid item xs={12} sm={4} key={idx}>
              <Paper sx={{ p: 3, textAlign: 'center' }}>
                <Box color="primary.main" mb={1}>{stat.icon}</Box>
                <Typography variant="h4" fontWeight={700}>{stat.value}</Typography>
                <Typography color="text.secondary">{stat.label}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Grid>
    </Grid>
  );

  // Profile Tab
  const renderProfile = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <Paper sx={{ p: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h6" fontWeight={600}>
              Personal Information
            </Typography>
            <IconButton onClick={() => setEditDialog('personal')}>
              <Edit />
            </IconButton>
          </Box>
          
          <Stack spacing={2}>
            <Box>
              <Typography variant="body2" color="text.secondary">Name</Typography>
              <Typography fontWeight={500}>
                {account.user.firstName} {account.user.lastName}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">Email</Typography>
              <Typography fontWeight={500}>{account.user.email}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">Phone</Typography>
              <Typography fontWeight={500}>{account.user.phone || 'Not set'}</Typography>
            </Box>
          </Stack>
        </Paper>
      </Grid>
      
      <Grid item xs={12} md={6}>
        <Paper sx={{ p: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h6" fontWeight={600}>
              Company Information
            </Typography>
            <IconButton onClick={() => setEditDialog('company')}>
              <Edit />
            </IconButton>
          </Box>
          
          <Stack spacing={2}>
            <Box>
              <Typography variant="body2" color="text.secondary">Company Name</Typography>
              <Typography fontWeight={500}>{account.company.name}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">Website</Typography>
              <Typography fontWeight={500}>{account.company.website || 'Not set'}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">Industry</Typography>
              <Typography fontWeight={500}>{account.company.industry}</Typography>
            </Box>
          </Stack>
        </Paper>
      </Grid>
      
      <Grid item xs={12}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight={600} mb={3}>
            Security
          </Typography>
          
          <List>
            <ListItem>
              <ListItemText
                primary="Password"
                secondary="Last changed 30 days ago"
              />
              <ListItemSecondaryAction>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Lock />}
                  onClick={() => setChangePasswordDialog(true)}
                >
                  Change Password
                </Button>
              </ListItemSecondaryAction>
            </ListItem>
            <Divider />
            <ListItem>
              <ListItemText
                primary="Two-Factor Authentication"
                secondary="Not enabled"
              />
              <ListItemSecondaryAction>
                <Button variant="outlined" size="small">
                  Enable 2FA
                </Button>
              </ListItemSecondaryAction>
            </ListItem>
          </List>
        </Paper>
      </Grid>
    </Grid>
  );

  // Billing Tab
  const renderBilling = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight={600} mb={3}>
            Payment Method
          </Typography>
          
          <Box
            sx={{
              p: 2,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <CreditCard sx={{ fontSize: 40, color: 'primary.main' }} />
            <Box flex={1}>
              <Typography fontWeight={600}>•••• •••• •••• 4242</Typography>
              <Typography variant="body2" color="text.secondary">
                Expires 12/2026
              </Typography>
            </Box>
            <Button size="small">Update</Button>
          </Box>
        </Paper>
      </Grid>
      
      <Grid item xs={12} md={6}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight={600} mb={3}>
            Billing Address
          </Typography>
          
          <Typography>
            {account.company.name}<br />
            123 Business St<br />
            San Francisco, CA 94102<br />
            United States
          </Typography>
          
          <Button size="small" sx={{ mt: 2 }}>
            Edit Address
          </Button>
        </Paper>
      </Grid>
      
      <Grid item xs={12}>
        <Paper sx={{ p: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h6" fontWeight={600}>
              Invoice History
            </Typography>
            <Button size="small">Download All</Button>
          </Box>
          
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Invoice</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {account.invoices.map((invoice) => (
                  <TableRow key={invoice.id}>
                    <TableCell>{invoice.id.toUpperCase()}</TableCell>
                    <TableCell>
                      {new Date(invoice.date).toLocaleDateString()}
                    </TableCell>
                    <TableCell>{formatPrice(invoice.amount)}</TableCell>
                    <TableCell>
                      <Chip
                        label={invoice.status.toUpperCase()}
                        color={getStatusColor(invoice.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small">
                        <Visibility />
                      </IconButton>
                      <IconButton size="small">
                        <Download />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Grid>
    </Grid>
  );

  // Settings Tab
  const renderSettings = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight={600} mb={3}>
            Notification Preferences
          </Typography>
          
          <List>
            {[
              { label: 'Lead notifications', desc: 'Get notified when new leads are generated' },
              { label: 'Weekly reports', desc: 'Receive weekly performance summaries' },
              { label: 'Billing alerts', desc: 'Get notified about payments and invoices' },
              { label: 'Product updates', desc: 'Stay informed about new features' },
            ].map((item, idx) => (
              <React.Fragment key={idx}>
                <ListItem>
                  <ListItemText primary={item.label} secondary={item.desc} />
                  <ListItemSecondaryAction>
                    <Chip label="Enabled" color="success" size="small" />
                  </ListItemSecondaryAction>
                </ListItem>
                {idx < 3 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </Paper>
      </Grid>
      
      <Grid item xs={12}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight={600} color="error" mb={2}>
            Danger Zone
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            These actions are irreversible. Please proceed with caution.
          </Typography>
          
          <Stack direction="row" spacing={2}>
            <Button variant="outlined" color="warning">
              Export All Data
            </Button>
            <Button variant="outlined" color="error">
              Delete Account
            </Button>
          </Stack>
        </Paper>
      </Grid>
    </Grid>
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: 4 }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Box mb={4}>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            Account Settings
          </Typography>
          <Typography color="text.secondary">
            Manage your account, billing, and preferences
          </Typography>
        </Box>
        
        {/* Tabs */}
        <Paper sx={{ mb: 3 }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab icon={<TrendingUp />} label="Overview" iconPosition="start" />
            <Tab icon={<Person />} label="Profile" iconPosition="start" />
            <Tab icon={<CreditCard />} label="Billing" iconPosition="start" />
            <Tab icon={<Settings />} label="Settings" iconPosition="start" />
          </Tabs>
        </Paper>
        
        {/* Tab Panels */}
        <TabPanel value={tabValue} index={0}>
          {renderOverview()}
        </TabPanel>
        <TabPanel value={tabValue} index={1}>
          {renderProfile()}
        </TabPanel>
        <TabPanel value={tabValue} index={2}>
          {renderBilling()}
        </TabPanel>
        <TabPanel value={tabValue} index={3}>
          {renderSettings()}
        </TabPanel>
      </Container>
      
      {/* Change Password Dialog */}
      <Dialog
        open={changePasswordDialog}
        onClose={() => setChangePasswordDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Change Password</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Current Password"
              type="password"
              fullWidth
            />
            <TextField
              label="New Password"
              type="password"
              fullWidth
            />
            <TextField
              label="Confirm New Password"
              type="password"
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setChangePasswordDialog(false)}>Cancel</Button>
          <Button variant="contained">Update Password</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AccountPage;
